import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  ErrorBanner,
  PageHeader,
  toast,
} from '@saas-kit/ui';
import type { PaginationState } from '@saas-kit/ui/DataTable';

import {
  createAnnouncement,
  listAnnouncements,
  type Announcement,
  type CreateAnnouncementPayload,
} from './api';
import { AnnouncementComposeForm } from './AnnouncementComposeForm';
import { AnnouncementsTable } from './AnnouncementsTable';
import { AnnouncementSummaryDrawer } from './AnnouncementSummaryDrawer';

const DEFAULT_PAGE_SIZE = 25;

export function AnnouncementsPage(): JSX.Element {
  const { t } = useTranslation('adminAnnouncements');
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [composeOpen, setComposeOpen] = useState(false);
  const [active, setActive] = useState<Announcement | null>(null);

  const query = useQuery({
    queryKey: ['admin-announcements', { offset, limit: pageSize }],
    queryFn: () => listAnnouncements({ offset, limit: pageSize }),
    placeholderData: (prev) => prev,
  });

  const createMutation = useMutation({
    mutationFn: createAnnouncement,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-announcements'] });
    },
    onError: (e) => toast.error(parseApiError(e).message),
  });

  async function handleCreate(payload: CreateAnnouncementPayload): Promise<void> {
    await createMutation.mutateAsync(payload);
  }

  const announcements = query.data?.items ?? [];
  const page = query.data?.page;
  const total = page?.total ?? 0;

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext: page ? page.offset + page.limit < page.total : false,
    hasPrev: offset > 0,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const showEmpty = !query.isLoading && !query.isError && total === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <Button onClick={() => setComposeOpen(true)}>
            {t('actions.compose')}
          </Button>
        }
      />

      {query.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}

      {showEmpty ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState title={t('empty.title')} description={t('empty.body')} />
          </CardContent>
        </Card>
      ) : (
        <AnnouncementsTable
          announcements={announcements}
          pagination={pagination}
          isLoading={query.isLoading}
          error={query.isError ? t('errors.load_failed') : null}
          onView={(announcement) => setActive(announcement)}
        />
      )}

      <AnnouncementComposeForm
        open={composeOpen}
        onOpenChange={setComposeOpen}
        isPending={createMutation.isPending}
        onSubmit={handleCreate}
      />

      {active ? (
        <AnnouncementSummaryDrawer
          announcement={active}
          open={Boolean(active)}
          onOpenChange={(o) => !o && setActive(null)}
        />
      ) : null}
    </div>
  );
}
